/**
 * Navigation functionality
 * Handles mobile nav panel, backdrop, and keyboard interactions
 * Uses Bootstrap Collapse + Dropdown for core interactivity
 * 
 * @package NCMARaleigh
 */

(function() {
    'use strict';

    var NCMARaleighNav = {
        
        /**
         * Initialize navigation
         */
        init: function() {
            this.mobileNav = document.getElementById('mobile-nav-panel');
            this.mobileToggle = document.querySelector('.mobile-nav-toggle');
            this.backdrop = null;
            
            if (this.mobileNav && this.mobileToggle) {
                this.createBackdrop();
                this.bindEvents();
                this.initMobileSubMenuToggle();
            }
            
            // Initialize mega menu hover behavior for desktop
            this.initMegaMenuHover();
        },
        
        /**
         * Create backdrop overlay element
         */
        createBackdrop: function() {
            this.backdrop = document.createElement('div');
            this.backdrop.className = 'mobile-nav-backdrop';
            this.backdrop.setAttribute('aria-hidden', 'true');
            document.body.appendChild(this.backdrop);
        },
        
        /**
         * Bind event listeners
         */
        bindEvents: function() {
            var self = this;
            
            // Listen for Bootstrap collapse events on mobile nav
            this.mobileNav.addEventListener('show.bs.collapse', function() {
                document.body.classList.add('mobile-nav-open');
                self.backdrop.classList.add('show');
            });

            // Focus first element after animation completes (element is fully visible)
            this.mobileNav.addEventListener('shown.bs.collapse', function() {
                self.activateFocusTrap(self.mobileNav);
            });

            this.mobileNav.addEventListener('hide.bs.collapse', function() {
                document.body.classList.remove('mobile-nav-open');
                self.backdrop.classList.remove('show');
                self.deactivateFocusTrap(self.mobileNav);
                // Reset all mobile submenus to collapsed state
                self.resetMobileSubmenus();
            });

            this.mobileNav.addEventListener('hidden.bs.collapse', function() {
                // Return focus to toggle button
                self.mobileToggle.focus();
            });
            
            // Close on backdrop click
            this.backdrop.addEventListener('click', function() {
                self.closeMobileNav();
            });
            
            // Close on Escape key
            document.addEventListener('keydown', function(e) {
                if (e.key === 'Escape') {
                    // Close mobile nav if open
                    if (self.mobileNav.classList.contains('show')) {
                        self.closeMobileNav();
                    }
                    // Close any open mega menu dropdowns
                    self.closeAllDropdowns();
                }
            });
        },
        
        /**
         * Close mobile navigation panel
         */
        closeMobileNav: function() {
            if (typeof bootstrap !== 'undefined' && bootstrap.Collapse) {
                var collapse = bootstrap.Collapse.getInstance(this.mobileNav);
                if (collapse) {
                    collapse.hide();
                }
            }
        },
        
        /**
         * Close all mega menu dropdowns
         */
        closeAllDropdowns: function() {
            var openDropdowns = document.querySelectorAll('.dropdown-menu.show');
            openDropdowns.forEach(function(dropdown) {
                dropdown.classList.remove('show');
                var toggle = dropdown.previousElementSibling;
                if (toggle) {
                    toggle.setAttribute('aria-expanded', 'false');
                }
            });
        },
        
        /**
         * Activate focus trap: move focus into element and cycle Tab within it.
         * Re-queries focusable elements on each keydown so submenus opening/closing
         * are always reflected in the tab order.
         */
        activateFocusTrap: function(element) {
            var getFocusable = function() {
                return Array.prototype.slice.call(element.querySelectorAll(
                    'button:not([disabled]), [href], input:not([disabled]), ' +
                    'select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
                ));
            };

            var focusable = getFocusable();
            if (focusable.length) focusable[0].focus();

            this._trapFocusHandler = function(e) {
                if (e.key !== 'Tab') return;
                var items = getFocusable();
                if (!items.length) return;
                var first = items[0];
                var last = items[items.length - 1];
                if (e.shiftKey) {
                    if (document.activeElement === first) {
                        last.focus();
                        e.preventDefault();
                    }
                } else {
                    if (document.activeElement === last) {
                        first.focus();
                        e.preventDefault();
                    }
                }
            };

            element.addEventListener('keydown', this._trapFocusHandler);
        },

        /**
         * Remove focus trap listener from element.
         */
        deactivateFocusTrap: function(element) {
            if (this._trapFocusHandler) {
                element.removeEventListener('keydown', this._trapFocusHandler);
                this._trapFocusHandler = null;
            }
        },
        
        /**
         * Clamp a mega menu dropdown to stay within the viewport.
         *
         * Called immediately before adding .show so positioning is correct
         * from the first animation frame. Modifier classes are reset on every
         * call, so future content/layout changes recalculate automatically.
         *
         * getBoundingClientRect() is accurate even while visibility:hidden
         * because the element is always in the layout flow.
         */
        clampDropdown: function(dropdown) {
            dropdown.classList.remove(
                'mega-menu__dropdown--align-left',
                'mega-menu__dropdown--align-right'
            );
            var rect = dropdown.getBoundingClientRect();
            if (rect.left < 0) {
                dropdown.classList.add('mega-menu__dropdown--align-left');
            } else if (rect.right > window.innerWidth) {
                dropdown.classList.add('mega-menu__dropdown--align-right');
            }
        },

        /**
         * Initialize mega menu behavior for desktop.
         *
         * All open/close goes through the same .show toggle so hover, click,
         * and keyboard all produce identical CSS-driven positioning.
         * Bootstrap Dropdown / Popper.js is intentionally not used here.
         */
        initMegaMenuHover: function() {
            var self = this;
            var megaItems = document.querySelectorAll('.mega-menu__item');
            
            megaItems.forEach(function(item) {
                var dropdown = item.querySelector('.mega-menu__dropdown');
                var toggle = item.querySelector('.dropdown-toggle');
                
                if (!dropdown || !toggle) return;
                
                // --- Mouse: open on hover ---
                item.addEventListener('mouseenter', function() {
                    if (window.innerWidth >= 1024) {
                        self.clampDropdown(dropdown);
                        dropdown.classList.add('show');
                        toggle.setAttribute('aria-expanded', 'true');
                    }
                });
                
                item.addEventListener('mouseleave', function() {
                    if (window.innerWidth >= 1024) {
                        dropdown.classList.remove('show');
                        toggle.setAttribute('aria-expanded', 'false');
                    }
                });
                
                // --- Mouse: clicking the caret toggles the dropdown ---
                toggle.addEventListener('click', function(e) {
                    if (window.innerWidth < 1024) return;
                    e.preventDefault();
                    var isOpen = dropdown.classList.contains('show');
                    self.closeAllDropdowns();
                    if (!isOpen) {
                        self.clampDropdown(dropdown);
                        dropdown.classList.add('show');
                        toggle.setAttribute('aria-expanded', 'true');
                    }
                });
                
                // --- Keyboard: Enter or Space opens/closes ---
                toggle.addEventListener('keydown', function(e) {
                    if (window.innerWidth < 1024) return;
                    if (e.key !== 'Enter' && e.key !== ' ') return;
                    e.preventDefault();
                    var isOpen = dropdown.classList.contains('show');
                    self.closeAllDropdowns();
                    if (!isOpen) {
                        self.clampDropdown(dropdown);
                        dropdown.classList.add('show');
                        toggle.setAttribute('aria-expanded', 'true');
                    }
                });
                
                // --- Keyboard: close when focus leaves the item entirely ---
                item.addEventListener('focusout', function() {
                    if (window.innerWidth < 1024) return;
                    // requestAnimationFrame lets the browser settle activeElement
                    // before we check whether focus moved outside this item.
                    requestAnimationFrame(function() {
                        if (!item.contains(document.activeElement)) {
                            dropdown.classList.remove('show');
                            toggle.setAttribute('aria-expanded', 'false');
                        }
                    });
                });
            });
        },
        
        /**
         * Initialize mobile submenu toggle functionality
         * Handles click to expand/collapse submenus with accordion behavior
         */
        initMobileSubMenuToggle: function() {
            var self = this;
            var toggles = this.mobileNav.querySelectorAll('.dropdown-toggle');
            
            toggles.forEach(function(toggle) {
                // Remove Bootstrap's data-bs-toggle to prevent conflict
                // Bootstrap Dropdown uses event delegation, so we need to remove the attribute
                toggle.removeAttribute('data-bs-toggle');
                
                toggle.addEventListener('click', function(e) {
                    e.preventDefault();
                    e.stopPropagation();
                    
                    var dropdown = this.nextElementSibling;
                    if (!dropdown || !dropdown.classList.contains('mega-menu__dropdown')) return;
                    
                    var isExpanded = dropdown.classList.contains('show');
                    
                    // Accordion behavior: close all other dropdowns first
                    self.resetMobileSubmenus();
                    
                    // Toggle the clicked dropdown (if it wasn't already open)
                    if (!isExpanded) {
                        dropdown.classList.add('show');
                        this.setAttribute('aria-expanded', 'true');
                    }
                });
            });
        },
        
        /**
         * Reset all mobile submenus to collapsed state
         */
        resetMobileSubmenus: function() {
            if (!this.mobileNav) return;
            
            var openDropdowns = this.mobileNav.querySelectorAll('.mega-menu__dropdown.show');
            openDropdowns.forEach(function(dropdown) {
                dropdown.classList.remove('show');
                var toggle = dropdown.previousElementSibling;
                if (toggle) {
                    toggle.setAttribute('aria-expanded', 'false');
                }
            });
        }
    };
    
    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function() {
            NCMARaleighNav.init();
        });
    } else {
        NCMARaleighNav.init();
    }
    
    // Expose to window for debugging
    window.NCMARaleighNav = NCMARaleighNav;
    
})();
